import React, { useState } from 'react';
import { Eye, EyeOff, ArrowLeft, Zap, ShieldCheck, BarChart2, Globe, LogIn } from 'lucide-react';
import api from '../../services/api';

type Mode = 'login' | 'reset-password';

export const LoginPage: React.FC = () => {
  const [mode, setMode] = useState<Mode>('login');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState('');

  const [resetUsername, setResetUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError('');
    setPwdLoading(true);
    try {
      await api.post('/auth/login', { username: email, password });
      window.location.href = '/dashboard';
    } catch (err: any) {
      const status = err?.response?.status;
      const data = err?.response?.data ?? {};
      if (status === 403 && data.requiresPasswordReset) {
        setResetUsername(email);
        setResetError('');
        setNewPassword('');
        setConfirmPassword('');
        setMode('reset-password');
        return;
      }
      if (status === 401) { setPwdError('Invalid credentials. Please try again.'); return; }
      if (status === 0 || !status) { setPwdError('Cannot reach the server. Check your connection.'); return; }
      setPwdError(data.message ?? 'Login failed. Please try again.');
    } finally { setPwdLoading(false); }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setResetError('Passwords do not match.'); return; }
    if (newPassword.length < 8) { setResetError('Password must be at least 8 characters.'); return; }
    setResetError('');
    setResetLoading(true);
    try {
      await api.post('/auth/update-password', { username: resetUsername, newPassword });
      window.location.href = '/dashboard';
    } catch (err: any) {
      const status = err?.response?.status;
      const msg: string = err?.response?.data?.message ?? '';
      if (status === 400) setResetError(msg || 'Password reset is not available for this account.');
      else if (status === 0 || !status) setResetError('Cannot reach the server.');
      else setResetError('Failed to reset password. Please try again.');
    } finally { setResetLoading(false); }
  };

  const features = [
    { Icon: Zap, text: 'Create and send offers in minutes' },
    { Icon: ShieldCheck, text: 'Secure digital acceptance with OTP' },
    { Icon: BarChart2, text: 'Real-time sales reports and analytics' },
  ];

  return (
    <div style={{ minHeight: '100vh', display: 'flex' }}>
      {/* ── Left brand panel ─────────────────────────────────────────────── */}
      <div style={{
        flex: 1,
        background: 'linear-gradient(160deg, #001815 0%, #003831 50%, #33605A 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '64px 56px',
      }}>
        {/* Decorative grid */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.07,
          backgroundImage: 'repeating-linear-gradient(0deg, white 0, white 1px, transparent 1px, transparent 48px), repeating-linear-gradient(90deg, white 0, white 1px, transparent 1px, transparent 48px)',
        }} />
        {/* Decorative circles */}
        <div style={{ position: 'absolute', width: 400, height: 400, top: -120, right: -120, borderRadius: '50%', border: '1px solid rgba(255,255,255,.12)' }} />
        <div style={{ position: 'absolute', width: 260, height: 260, bottom: 60, left: -80, borderRadius: '50%', border: '1px solid rgba(255,255,255,.08)' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ marginBottom: 56 }}>
            <img src="/salam-logo.svg" alt="Salam" style={{ height: 44 }} />
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', marginTop: 4, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              SME Reseller Portal
            </div>
          </div>

          <h1 style={{ fontSize: 36, fontWeight: 800, color: '#fff', lineHeight: 1.15, marginBottom: 18, maxWidth: 400, margin: '0 0 18px' }}>
            Grow your business with Salam connectivity
          </h1>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,.72)', lineHeight: 1.65, maxWidth: 380, marginBottom: 48 }}>
            Manage offers, close deals, and track your reseller performance — all in one place.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {features.map(({ Icon, text }) => (
              <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'rgba(255,255,255,.85)' }}>
                <div style={{ width: 28, height: 28, background: 'rgba(255,255,255,.15)', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={14} />
                </div>
                <span style={{ fontSize: 13.5 }}>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right form panel ──────────────────────────────────────────────── */}
      <div style={{
        width: 480, minWidth: 480,
        background: '#fff',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '56px 48px',
        overflowY: 'auto',
      }}>
        {mode === 'login' ? (
          <>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40 }}>
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-1)' }}>Sign in</div>
                <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 3 }}>Welcome back to your portal</div>
              </div>
              <button style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 20, border: '1.5px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', cursor: 'pointer', background: '#fff', fontFamily: 'inherit' }}>
                <Globe size={13} />
                EN
              </button>
            </div>

            {/* Error banner */}
            {pwdError && (
              <div style={{ background: 'var(--err-bg)', border: '1px solid var(--err-border)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: 'var(--err-hover)', marginBottom: 20 }}>
                {pwdError}
              </div>
            )}

            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <label className="lbl">Email address</label>
                <input className="inp" type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="username" required />
                <span className="hint">Your registered reseller account email</span>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                  <label className="lbl" style={{ marginBottom: 0 }}>Password</label>
                  <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--p)', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit', padding: 0 }}>
                    Forgot password?
                  </button>
                </div>
                <div style={{ position: 'relative' }}>
                  <input className="inp" type={showPwd ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" autoComplete="current-password" style={{ paddingRight: 44 }} required />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', display: 'flex' }}>
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" id="remember-me" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={{ width: 15, height: 15, accentColor: 'var(--p)' }} />
                <label htmlFor="remember-me" style={{ fontSize: 13, color: 'var(--text-2)', cursor: 'pointer' }}>Keep me signed in for 30 days</label>
              </div>

              <button type="submit" disabled={pwdLoading} style={{
                width: '100%', padding: '13px', background: 'var(--p)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: pwdLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, transition: 'all .15s', marginTop: 6,
                opacity: pwdLoading ? 0.7 : 1,
              }}
                onMouseEnter={(e) => { if (!pwdLoading) { (e.currentTarget as HTMLElement).style.background = 'var(--p-hover)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--p)'; (e.currentTarget as HTMLElement).style.transform = ''; }}
              >
                <LogIn size={16} />
                {pwdLoading ? 'Signing in…' : 'Sign in to Portal'}
              </button>
            </form>

            <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--border)', textAlign: 'center', fontSize: 12, color: 'var(--text-3)' }}>
              Having trouble?{' '}
              <a href="mailto:rms-support@salam.sa" style={{ color: 'var(--p)', fontWeight: 500, textDecoration: 'none' }}>Contact support</a>
              <span style={{ margin: '0 6px' }}>·</span>
              <span style={{ color: 'var(--text-3)', cursor: 'pointer' }}>Privacy Policy</span>
            </div>
          </>
        ) : (
          <>
            <button type="button" onClick={() => { setMode('login'); setPwdError(''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, padding: 0, marginBottom: 24, fontFamily: 'inherit' }}>
              <ArrowLeft size={14} />
              Back to login
            </button>

            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-1)', marginBottom: 4 }}>Set new password</div>
            <div style={{ fontSize: 13.5, color: 'var(--text-2)', marginBottom: 28 }}>Your account requires a password update before continuing.</div>

            {resetError && (
              <div style={{ background: 'var(--err-bg)', border: '1px solid var(--err-border)', borderRadius: 10, padding: '12px 16px', fontSize: 13.5, color: 'var(--err-hover)', marginBottom: 20 }}>
                {resetError}
              </div>
            )}

            <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <label className="lbl">Username</label>
                <input className="inp" type="text" value={resetUsername} onChange={(e) => setResetUsername(e.target.value)} style={{ background: 'var(--surface-alt)' }} required />
              </div>
              <div>
                <label className="lbl">New Password</label>
                <div style={{ position: 'relative' }}>
                  <input className="inp" type={showNewPwd ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Minimum 8 characters" autoComplete="new-password" style={{ paddingRight: 44 }} required />
                  <button type="button" onClick={() => setShowNewPwd(!showNewPwd)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', display: 'flex' }}>
                    {showNewPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="lbl">Confirm New Password</label>
                <input className="inp" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" autoComplete="new-password" required />
              </div>
              <button type="submit" disabled={resetLoading} style={{
                width: '100%', padding: '13px', background: 'var(--p)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: resetLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, marginTop: 6, opacity: resetLoading ? 0.7 : 1,
              }}>
                {resetLoading ? 'Updating…' : 'Update Password & Sign In'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
